




























































































import {
  Component,
  Prop,
  Model,
  Watch,
  Vue as VueDecorator,
} from "vue-property-decorator";

import StateButtonComponent, {
  ButtonState,
  getButtonStateColor,
  getButtonStateClass,
} from "@/components/common/StateButton.vue";
import SearchFieldComponent from "@/components/common/SearchField.vue";
import { COLOR_BUTTON_GREEN, COLOR_BUTTON_RED } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { NoteApartmentInfo, NoteRoomInfo } from "@/data/models/ProjectLog";

/** Show the search field only once the list is long enough to warrant it. */
const SEARCH_THRESHOLD = 8;

@Component({
  components: {
    "state-button": StateButtonComponent,
    "search-field": SearchFieldComponent,
  },
})
export default class PickerDialogComponent<T> extends VueDecorator {
  @Prop({ default: null }) buttonState!: ButtonState | null;
  @Prop() options!: Array<T>;
  @Prop({ default: true }) showSelectNothingOption!: boolean;
  @Prop({ default: false }) hideDetails!: boolean;
  @Prop({ default: false }) readonly!: boolean;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: false }) hyDryClientConfig!: boolean;
  @Prop({ default: true }) closeOnSelect!: boolean;
  @Prop({ default: "560px" }) width!: string;
  @Prop({ default: true }) transformText!: boolean;
  @Prop({ default: false }) isForApartmentSelection!: boolean;
  @Prop({ default: false }) isForRoomSelection!: boolean;
  @Prop({
    default: () => {
      return [];
    },
  })

  errors!: Array<string>;
  isPickerOpen = false;

  @Model("change") current!: T;
  @Prop() label!: string;
  @Prop() formatter!: (toFormat: T) => string;

  searchQuery = "";

  get showSearch() {
    return this.options.length > SEARCH_THRESHOLD;
  }

  get filteredOptions() {
    const query = this.searchQuery.toLowerCase().trim();

    if (query === "") {
      return this.options;
    }

    return this.options.filter(
      (item) => this.getString(item).toLowerCase().indexOf(query) >= 0
    );
  }

  onSearchQueryChanged(searchQuery: string) {
    this.searchQuery = searchQuery;
  }

  @Watch("isPickerOpen")
  onPickerOpenChanged(isOpen: boolean) {
    if (isOpen) {
      this.searchQuery = "";
    }
  }

  get styleClass() {
    if (this.hyDryClientConfig || !this.transformText) {
      return 'lower-case white-text';
    }

    return 'white-text';
  }

  get showStateButton() {
    return this.buttonState !== null;
  }

  get stateColor() {
    return themedFill(getButtonStateColor(this.buttonState ?? ButtonState.RED));
  }

  get buttonClass() {
    const state = this.buttonState ?? ButtonState.RED;

    return themedFillTextClass(getButtonStateColor(state), getButtonStateClass(state));
  }

  /** An option's fill for the active theme: in dark mode the selected green and the red take the palette shades. */
  getItemColor(item: T) {
    return themedFill(this.getColor(item));
  }

  /** The option's classes, with its white label swapped for the readable one on a dark-mode fill. */
  getItemClass(item: T) {
    return this.styleClass.replace("white-text", themedFillTextClass(this.getColor(item), "white-text"));
  }

  get selectedString() {
    if (this.current !== null && this.current !== undefined) {
      return this.getString(this.current);
    } else {
      return "";
    }
  }

  getColor(item: T) {
    if (this.isForApartmentSelection) {
      const selectedApartment = this.current as unknown as NoteApartmentInfo;
      const currentItem = item as unknown as NoteApartmentInfo;

      return selectedApartment?.contactPersonId === currentItem?.contactPersonId ? COLOR_BUTTON_GREEN : COLOR_BUTTON_RED;
    }
    else if (this.isForRoomSelection) {
      const selectedRoom = this.current as unknown as NoteRoomInfo;
      const currentItem = item as unknown as NoteRoomInfo;

      const isCurrentItemSelected = selectedRoom?.contactPersonId === currentItem?.contactPersonId &&
        selectedRoom?.roomId === currentItem?.roomId;

      return isCurrentItemSelected ? COLOR_BUTTON_GREEN : COLOR_BUTTON_RED;
    }

    return this.current === item ? COLOR_BUTTON_GREEN : COLOR_BUTTON_RED;
  }

  getString(item: T, shouldTransform?: boolean) {
    if (this.hyDryClientConfig && shouldTransform) {
      const textParts = this.formatter(item).split("(");
      const newText = textParts[0].toUpperCase() + "(" + textParts[1];

      return newText;
    }
    return this.formatter(item);
  }

  onBack() {
    this.isPickerOpen = false;
  }

  onSelectNothing() {
    this.onSelectedChanged();
  }

  openPicker() {
    this.isPickerOpen = true;
  }

  onItemSelected(item: T) {
    if (item !== this.current) {
      if (this.closeOnSelect) {
        this.isPickerOpen = false;
      }
      this.$emit("newItemPicked", item);
    } else {
      this.isPickerOpen = false;
    }

    this.$emit("change", item);
    this.$emit("isValueSelected");
  }

  onSelectedChanged() {
    this.isPickerOpen = false;
    this.$emit("change", null);
    this.$emit("isValueSelected");
  }
}
